import React, { useMemo } from "react";
import cn from "clsx";
import { useTranslation } from "react-i18next";
import { getDeepSelectedCategory } from "@/utils";
import { FILTER_TYPES } from "../constants";
import { CategoryFilter, GeneralFacet } from "../types";

const getDeepLeaves = (item: any): any[] => {
  const children = item?.children;
  if (!Array.isArray(children) || children.length === 0) return [item];
  const hasNested = children.some(
    c => Array.isArray(c?.children) && c.children.length > 0
  );
  return hasNested ? children.flatMap(getDeepLeaves) : children;
};

const computeSizeKeys = (filterData: any): Set<string> => {
  const adv = Array.isArray(filterData?.advanced) ? filterData.advanced : [];
  const sim = Array.isArray(filterData?.simple) ? filterData.simple : [];
  const groups = Array.isArray(filterData) ? filterData : [...adv, ...sim];
  const leaves = groups.flatMap(getDeepLeaves);
  return new Set(
    leaves
      .filter((l: any) => l?.count == null || Number(l.count) > 0)
      .map((l: any) => l?.key)
      .filter(Boolean)
  );
};

const priceRange = filter => {
  if (filter.type !== FILTER_TYPES.PRICE) return null;
  const priceLabelText = `(${filter?.data?.currency} ${filter?.data?.selectedRange?.min}-${filter?.data?.selectedRange?.max})`;
  return <strong style={{ marginRight: 0 }}>{priceLabelText}</strong>;
};

const getSelectedFilterCount = (
  filter,
  selectedFilters,
  availableKeysMap: Map<string, Set<string>>
) => {
  if (filter.type === FILTER_TYPES.PRICE) return undefined;

  if (filter.type === FILTER_TYPES.CATEGORY) {
    return getDeepSelectedCategory(filter as CategoryFilter).length;
  }

  if (filter.type === FILTER_TYPES.SIZE) {
    const allSelected = selectedFilters?.size?.selectedOptions;
    if (!Array.isArray(allSelected)) return 0;
    const categoryKey = filter?.parentCategoryKey;
    if (!categoryKey) return allSelected.length;
    const prefix = `${categoryKey}+`;
    const availableKeys = availableKeysMap.get(categoryKey);
    if (!availableKeys) return 0;
    return allSelected.filter(
      opt =>
        typeof opt?.key === "string" &&
        opt.key.startsWith(prefix) &&
        availableKeys.has(opt.key)
    ).length;
  }

  return selectedFilters?.[filter.queryParam]?.selectedOptions?.length ?? 0;
};

const FiltersTypeSection = ({
  filters,
  selectedFilters,
  toggledFilter,
  setToggledFilter,
  onFilterClick
}) => {
  const { t } = useTranslation("productListing");

  const availableKeysMap = useMemo(() => {
    const map = new Map<string, Set<string>>();
    (filters || []).forEach((filter: any) => {
      if (filter.type === FILTER_TYPES.SIZE && filter.parentCategoryKey) {
        map.set(filter.parentCategoryKey, computeSizeKeys(filter.data));
      }
    });
    return map;
  }, [filters]);

  const noSortingFilter = filters.filter(
    filter =>
      filter.type !== "singleSelect" &&
      filter.queryParam !== "sort" &&
      filter.type !== FILTER_TYPES.MORE_FILTERS
  );

  const moreFiltersContainer = (filters as any[]).find(
    (f: any) => f.type === FILTER_TYPES.MORE_FILTERS
  );
  const moreFilterFacets: any[] = Array.isArray(moreFiltersContainer?.data)
    ? moreFiltersContainer.data
    : [];

  const sortedFilters = noSortingFilter.sort(
    (a, b) => a.sortOrder - b.sortOrder
  );

  const isCategorySelected = moreFilterFacets.length > 0;
  const selectedCategoryLabel =
    moreFiltersContainer?.subcategory?.label?.toUpperCase() ?? null;

  const isMoreFiltersGroupActive =
    (toggledFilter as any)?.type === FILTER_TYPES.MORE_FILTERS ||
    moreFilterFacets.some(
      f => f.queryParam === (toggledFilter as any)?.queryParam
    );

  const sizeFacets = sortedFilters.filter(f => f.type === FILTER_TYPES.SIZE);

  const isAnySizeActive = toggledFilter?.type === FILTER_TYPES.SIZE;

  const totalSizeCount = sizeFacets.reduce(
    (acc, f) =>
      acc + (getSelectedFilterCount(f, selectedFilters, availableKeysMap) || 0),
    0
  );

  const handleClick = (filter: any) =>
    typeof onFilterClick === "function"
      ? onFilterClick(filter)
      : setToggledFilter(filter);

  const handleSizeGroupClick = () => {
    if (sizeFacets.length > 0) handleClick(sizeFacets[0]);
  };

  type RenderItem = { kind: "regular"; filter: any } | { kind: "sizeGroup" };

  let sizeGroupInserted = false;
  const renderList: RenderItem[] = sortedFilters.reduce((acc, filter) => {
    if (filter.type === FILTER_TYPES.SIZE) {
      if (!sizeGroupInserted) {
        acc.push({ kind: "sizeGroup" });
        sizeGroupInserted = true;
      }
    } else {
      acc.push({ kind: "regular", filter });
    }
    return acc;
  }, [] as RenderItem[]);

  return (
    <div className="filters_titles_section">
      {renderList.map((item, index) => {
        if (item.kind === "sizeGroup") {
          return (
            <React.Fragment key="size-group">
              <div
                className={cn("filter_title_wrapper", "size_group", {
                  is_active: isAnySizeActive
                })}
                onClick={handleSizeGroupClick}
              >
                <span>{t("size")}</span>
                {!!totalSizeCount && (
                  <span className="selected_filter_count">
                    {totalSizeCount}
                  </span>
                )}
              </div>
              {isAnySizeActive &&
                sizeFacets.map((sizeFacet, si) => {
                  const isActiveSub =
                    sizeFacet?.defaultLabel?.toLowerCase() ===
                    toggledFilter?.defaultLabel?.toLowerCase();
                  const count = getSelectedFilterCount(
                    sizeFacet,
                    selectedFilters,
                    availableKeysMap
                  );
                  return (
                    <div
                      key={`size-sub-${si}`}
                      className={cn(
                        "filter_title_wrapper",
                        "filter_size_sub_item",
                        {
                          is_active: isActiveSub
                        }
                      )}
                      onClick={() => handleClick(sizeFacet)}
                    >
                      <span>{sizeFacet.label}</span>
                      {!!count && (
                        <span className="selected_filter_count">{count}</span>
                      )}
                    </div>
                  );
                })}
            </React.Fragment>
          );
        }

        const filter = item.filter;
        const isActiveFilter =
          filter?.defaultLabel?.toLowerCase() ===
          toggledFilter?.defaultLabel?.toLowerCase();
        const selectedFilterCount = getSelectedFilterCount(
          filter,
          selectedFilters,
          availableKeysMap
        );

        return (
          <div
            key={`${index}${filter.label.toLowerCase()}`}
            className={cn("filter_title_wrapper", {
              is_active: isActiveFilter
            })}
            onClick={() => handleClick(filter)}
          >
            <span>
              {filter.label}
              {priceRange(filter)}
            </span>
            {!!selectedFilterCount && (
              <span className="selected_filter_count">
                {selectedFilterCount}
              </span>
            )}
          </div>
        );
      })}

      {isCategorySelected ? (
        <>
          <div className="filter_title_wrapper more_filters_category_label">
            <span>{selectedCategoryLabel}</span>
          </div>
          {moreFilterFacets.map((filter: any, i: number) => {
            const isActive =
              filter.queryParam === (toggledFilter as any)?.queryParam;
            const count =
              selectedFilters?.[filter.queryParam]?.selectedOptions?.length ??
              0;
            return (
              <div
                key={i}
                className={cn("filter_title_wrapper", { is_active: isActive })}
                onClick={() => handleClick(filter)}
              >
                <span>{filter.label}</span>
                {!!count && (
                  <span className="selected_filter_count">{count}</span>
                )}
              </div>
            );
          })}
        </>
      ) : moreFiltersContainer ? (
        <div
          className={cn("filter_title_wrapper more_filters_btn", {
            is_active: isMoreFiltersGroupActive
          })}
          onClick={() => handleClick(moreFiltersContainer)}
        >
          <span>{moreFiltersContainer.label ?? t("moreFilters")}</span>
        </div>
      ) : null}
    </div>
  );
};

export default FiltersTypeSection;
